
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ProfileState } from "@/integrations/supabase/profile-types";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserRound, Lock, Camera, Key, MessageSquare } from "lucide-react";
import { TabContents } from "@/components/profile/TabContents";
import { parseRates, parseServices } from "@/utils/profile-utils";
import { Json } from "@/integrations/supabase/types/base";

const defaultProfile: ProfileState = {
  status: 'offline',
  description: '',
  rates: {
    "30min": { incall: null, outcall: null },
    "1hour": { incall: null, outcall: null },
    overnight: { incall: null, outcall: null }
  },
  services: [],
  is_description_locked: false,
  is_rates_locked: false,
  is_services_locked: false,
  chatbot_character: '',
  chatbot_knowledge: '',
  chatbot_style: ''
};

const Profile = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<ProfileState>(defaultProfile);
  const [activeTab, setActiveTab] = useState("api");

  useEffect(() => {
    checkUser();
    loadProfile();
  }, []);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/login');
      return;
    }
  };

  const loadProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      const { data, error } = await supabase
        .from('profile_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        const { data: newProfile, error: createError } = await supabase
          .from('profile_settings')
          .insert({
            user_id: user.id,
            status: defaultProfile.status,
            description: defaultProfile.description,
            rates: defaultProfile.rates as unknown as Json,
            services: defaultProfile.services,
            is_description_locked: defaultProfile.is_description_locked,
            is_rates_locked: defaultProfile.is_rates_locked,
            is_services_locked: defaultProfile.is_services_locked,
            chatbot_character: defaultProfile.chatbot_character,
            chatbot_knowledge: defaultProfile.chatbot_knowledge,
            chatbot_style: defaultProfile.chatbot_style
          })
          .select()
          .single();

        if (createError) throw createError;
        
        setProfile(defaultProfile);
      } else {
        setProfile({
          status: data.status || 'offline',
          description: data.description || '',
          rates: parseRates(data.rates),
          services: parseServices(data.services),
          is_description_locked: !!data.is_description_locked,
          is_rates_locked: !!data.is_rates_locked,
          is_services_locked: !!data.is_services_locked,
          chatbot_character: data.chatbot_character || '',
          chatbot_knowledge: data.chatbot_knowledge || '',
          chatbot_style: data.chatbot_style || ''
        });
      }
    } catch (error: any) {
      console.error('Error loading profile:', error);
      toast({
        title: "Error loading profile",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async (updatedProfile: ProfileState) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      const { error } = await supabase
        .from('profile_settings')
        .upsert({
          user_id: user.id,
          status: updatedProfile.status,
          description: updatedProfile.description,
          rates: updatedProfile.rates as unknown as Json,
          services: updatedProfile.services,
          is_description_locked: updatedProfile.is_description_locked,
          is_rates_locked: updatedProfile.is_rates_locked,
          is_services_locked: updatedProfile.is_services_locked,
          chatbot_character: updatedProfile.chatbot_character,
          chatbot_knowledge: updatedProfile.chatbot_knowledge,
          chatbot_style: updatedProfile.chatbot_style
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error saving profile",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-hotbot-pink" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 space-y-8 min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      <Card className="bg-white/90 backdrop-blur-lg shadow-lg border-pink-100">
        <CardHeader className="bg-gradient-to-r from-hotbot-pink to-hotbot-purple text-white rounded-t-lg">
          <CardTitle className="text-center relative text-2xl">
            <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
              <UserRound className="w-8 h-8" />
            </div>
            Profile Settings
            <p className="text-xs mt-2 text-white/80">Customize your HotBot experience</p>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid grid-cols-5 gap-4 bg-pink-50 p-2 rounded-lg">
              <TabsTrigger value="api" className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                API
              </TabsTrigger>
              <TabsTrigger value="profile" className="flex items-center gap-2">
                <UserRound className="w-4 h-4" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="security" className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Security
              </TabsTrigger>
              <TabsTrigger value="sms" className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                SMS
              </TabsTrigger>
              <TabsTrigger value="media" className="flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Media
              </TabsTrigger>
            </TabsList>

            <TabContents profile={profile} onSave={saveProfile} />
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
